import { useIonRouter } from "@ionic/react";
import { ReactElement, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useStudySlug } from "../../../../hooks/useStudySlug";
import { getVideosByUids, VideoMeta } from "../../../../services/video.service";
import VideoCard from "../../../molecules/VideoCard/VideoCard";

interface VideoCardsData {
  uids?: string[];
  caption?: string;
}

/**
 * A grid of uploaded videos, rendered with the SAME card as the Videos tab
 * (components/molecules/VideoCard) — thumbnail, hover preview, save toggle — so the two
 * surfaces can't drift apart.
 *
 * Only uids are authored: titles and thumbnails are read live, so renaming a video
 * renames its card everywhere. The fetch is arm-scoped server-side, so a video the
 * member's arm excludes silently drops out of the grid instead of 404-ing on tap.
 */

// Mirrors pages/Videos/components/VideoGrid: two columns on a phone, then as many
// 180–220px cards as the width allows, wrapping onto new lines. The page padding and
// background stay OUT — inside content the block sits in the reading column.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(180px, 220px));
    justify-content: center;
  }
`;

const Caption = styled.p`
  font-size: 13px;
  color: rgba(var(--app-color-text-rgb, 4, 23, 35), 0.6);
  margin: 6px 0 0;
  text-align: center;
`;

const VideoCardsBlock = ({ data }: { data: VideoCardsData }): ReactElement | null => {
  const uids = (data.uids ?? []).filter(Boolean);
  // Route param first, configured slug as the fallback — the same resolution every other
  // navigating page uses. Reading it off the config alone can hand back "" on a
  // slug-less entry, which silently turned every card tap into a no-op.
  const studySlug = useStudySlug();
  const ionRouter = useIonRouter();

  const [videos, setVideos] = useState<VideoMeta[]>([]);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  // Keyed on the joined uids, not the array: the block's data object is rebuilt on every
  // render of the page, so an array identity dep would refetch forever.
  const uidKey = uids.join(",");

  useEffect(() => {
    if (!uidKey) return undefined;

    let active = true;
    getVideosByUids(uidKey.split(","))
      .then((items) => {
        if (!active) return;
        const byUid = new Map(items.map((item) => [item.uid, item]));
        // Authored order — the API returns library order, and dropped uids (deleted, or
        // excluded by the member's arm) just leave the grid shorter.
        setVideos(uidKey.split(",").map((uid) => byUid.get(uid)).filter(Boolean) as VideoMeta[]);
      })
      .catch(() => {
        if (active) setVideos([]);
      });

    return () => {
      active = false;
    };
  }, [uidKey]);

  if (!uids.length || !videos.length) return null;

  const openVideo = async (uid: string): Promise<void> => {
    // This grid can be rendered INSIDE an open sheet — a video's own rich description is
    // the obvious case. Pushing a route under an open modal navigates the page behind it
    // and nothing appears to happen, so the sheet has to be dismissed first. Generic on
    // purpose (closest ion-modal), so any future modal host works the same way.
    const modal = gridRef.current?.closest("ion-modal") as HTMLIonModalElement | null;
    if (modal) {
      try {
        await modal.dismiss();
      } catch {
        /* already dismissing — carry on and navigate */
      }
    }

    // Same destination as tapping a card on the Videos tab (buildVideoNavigationPath's
    // no-island form): the full-screen player, swipeable through the library.
    ionRouter.push(`/${studySlug}/tabs/videos/${uid}/default`, "forward", "push");
  };

  return (
    <div>
      <Grid ref={gridRef}>
        {videos.map((video) => (
          <VideoCard
            key={video.uid}
            video={video}
            isHover={hoverId === video.uid}
            onHoverStart={() => setHoverId(video.uid)}
            onHoverEnd={() => setHoverId((current) => (current === video.uid ? null : current))}
            onClick={() => void openVideo(video.uid)}
          />
        ))}
      </Grid>
      {data.caption ? <Caption>{data.caption}</Caption> : null}
    </div>
  );
};

export default VideoCardsBlock;
